/**
 * Utilidades para manejar permisos y lógica de ejecuciones
 * relacionadas con permitir_relacion_a_multiples_ejecuciones
 */

/**
 * Determina si un proceso debe ser individual basado en el crédito fiscal y estatus
 * @param {Object} creditoFiscal - Objeto del crédito fiscal
 * @param {Object} estatusSeleccionado - Estatus de ejecución seleccionado
 * @returns {boolean} - true si el proceso debe ser individual
 */
export const isProcesoIndividual = (creditoFiscal, estatusSeleccionado) => {
  const { permitir_relacion_a_multiples_ejecuciones = false } = creditoFiscal?.[0] ?? {};

  return (
    (estatusSeleccionado?.asigna_credito_fiscal && !permitir_relacion_a_multiples_ejecuciones)
    || estatusSeleccionado?.adjunta_documentos
  );
};

/**
 * Determina qué ejecuciones se van a aplicar basado en el tipo de proceso
 * @param {boolean} isIndividual - Si el proceso es individual
 * @param {Array} ejecuciones - Lista de ejecuciones disponibles
 * @returns {Array} - Ejecuciones que se van a aplicar
 */
export const getEjecucionesToApply = (isIndividual, ejecuciones) => (isIndividual
  ? [ejecuciones[0]] : ejecuciones);

/**
 * Determina si se debe mostrar la alerta de proceso individual
 * @param {boolean} isIndividual - Si el proceso es individual
 * @param {number} totalEjecuciones - Total de ejecuciones disponibles
 * @param {Object} creditoFiscal - Objeto del crédito fiscal
 * @param {Object} estatusEjecucion - Estatus de ejecución
 * @returns {boolean} - true si se debe mostrar la alerta
 */
export const shouldShowIndividualAlert = (
  isIndividual,
  totalEjecuciones,
  creditoFiscal,
  estatusEjecucion,
) => {
  if (estatusEjecucion?.asigna_credito_fiscal && !creditoFiscal?.length) {
    return false;
  }

  return isIndividual && totalEjecuciones > 1;
};

/**
 * Determina si se debe mostrar la alerta de múltiples ejecuciones
 * @param {Object} creditoFiscal - Objeto del crédito fiscal
 * @returns {boolean} - true si se debe mostrar la alerta
 */
export const shouldShowMultipleAlert = (creditoFiscal) => creditoFiscal
  ?.[0]?.permitir_relacion_a_multiples_ejecuciones;

/**
 * Genera el mensaje de alerta para proceso individual
 * @param {boolean} shouldShow - Si se debe mostrar la alerta
 * @param {string} estatusDescripcion - Descripción del estatus
 * @returns {string|null} - Mensaje de alerta o null
 */
export const getIndividualAlertMessage = (shouldShow, estatusDescripcion) => {
  if (!shouldShow || !estatusDescripcion) return null;

  return `El estatus "${estatusDescripcion}" no puede ser aplicado a múltiples reportes morosos simultáneamente. Por favor, seleccione un reporte moroso y vuelva a intentar.`;
};

/**
 * Genera el mensaje de alerta para múltiples ejecuciones
 * @param {boolean} shouldShow - Si se debe mostrar la alerta
 * @param {string} claveCredito - Clave del crédito fiscal
 * @returns {string|null} - Mensaje de alerta o null
 */
export const getMultipleAlertMessage = (shouldShow, claveCredito) => {
  if (!shouldShow || !claveCredito) return null;

  return 'El estatus seleccionado se aplicará a todos los reportes morosos. Este crédito permite procesar múltiples reportes morosos de manera simultánea.';
};

/**
 * Obtiene información resumida sobre los permisos de ejecución
 * @param {Object} creditoFiscal - Objeto del crédito fiscal
 * @param {Object} estatusSeleccionado - Estatus de ejecución seleccionado
 * @param {Array} ejecuciones - Lista de ejecuciones disponibles
 * @returns {Object} - Objeto con información de permisos
 */
export const getEjecucionPermissionsInfo = (creditoFiscal, estatusSeleccionado, ejecuciones) => {
  const isIndividual = isProcesoIndividual(creditoFiscal, estatusSeleccionado);
  const ejecucionesToApply = getEjecucionesToApply(isIndividual, ejecuciones);
  const showIndividualAlert = shouldShowIndividualAlert(
    isIndividual,
    ejecuciones.length,
    creditoFiscal,
    estatusSeleccionado,
  );
  const showMultipleAlert = shouldShowMultipleAlert(creditoFiscal);

  return {
    isIndividualProcess: isIndividual,
    ejecucionesToApply,
    shouldShowIndividualAlert: showIndividualAlert,
    shouldShowMultipleAlert: showMultipleAlert,
    canApplyToMultiple: !isIndividual,
    totalEjecuciones: ejecuciones.length,
    ejecucionesAplicadas: ejecucionesToApply.length,
  };
};
